import Fuse from 'fuse.js';
import type { Deal } from '../../types/deals';

export type DiscountBucket = 20 | 40 | 60;

export type DealFilters = {
  vendor: string | null;
  discount: DiscountBucket | null;
  hasCode: boolean;
  expiringSoon: boolean;
  latest?: boolean;
};

export type DealFilterFields = Pick<
  Deal,
  | 'retailer'
  | 'retailerDisplayName'
  | 'name'
  | 'description'
  | 'code'
  | 'expires_at'
  | 'effective_at'
  | 'image_url'
>;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const END_OF_DAY_MS = 24 * 60 * 60 * 1000 - 1;
/** Deals that expire within this window count as “expiring soon”. */
export const EXPIRING_SOON_MS = 7 * 24 * 60 * 60 * 1000;
/** Deals that started within this window count as “latest”. */
export const LATEST_DEAL_MS = 7 * 24 * 60 * 60 * 1000;

export const DEAL_COLLECTION_SLUGS = [
  'latest',
  'expiring-soon',
  'promo-codes',
  '20-off',
  '40-off',
  '60-off',
] as const;

export type DealCollectionSlug = (typeof DEAL_COLLECTION_SLUGS)[number];

export type DealCollection = {
  slug: DealCollectionSlug;
  latest: boolean;
  expiringSoon: boolean;
  hasCode: boolean;
  discount: DiscountBucket | null;
};

const DEAL_COLLECTIONS: Record<DealCollectionSlug, Omit<DealCollection, 'slug'>> = {
  latest: { latest: true, expiringSoon: false, hasCode: false, discount: null },
  'expiring-soon': { latest: false, expiringSoon: true, hasCode: false, discount: null },
  'promo-codes': { latest: false, expiringSoon: false, hasCode: true, discount: null },
  '20-off': { latest: false, expiringSoon: false, hasCode: false, discount: 20 },
  '40-off': { latest: false, expiringSoon: false, hasCode: false, discount: 40 },
  '60-off': { latest: false, expiringSoon: false, hasCode: false, discount: 60 },
};

export function isDealCollectionSlug(slug: string | undefined | null): slug is DealCollectionSlug {
  return (
    typeof slug === 'string' &&
    (DEAL_COLLECTION_SLUGS as readonly string[]).includes(slug.toLowerCase())
  );
}

export function parseDealCollection(raw: string | undefined | null): DealCollection | null {
  if (!raw?.trim()) return null;
  const slug = raw.trim().toLowerCase();
  if (!isDealCollectionSlug(slug)) return null;
  return { slug, ...DEAL_COLLECTIONS[slug] };
}

/** Milliseconds for `expires_at`; date-only values are end-of-day, matching ingestion. */
export function expiryTimestamp(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null;
  const ms = Date.parse(expiresAt);
  if (Number.isNaN(ms)) return null;
  return DATE_ONLY.test(expiresAt) ? ms + END_OF_DAY_MS : ms;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export type ExpiryCountdown = { unit: 'days' | 'hours'; count: number } | { unit: 'withinHour' };

/** Whole days or hours left, rounded down so a deal never looks longer-lived than it is. */
export function expiryCountdown(
  expiresAt: string | null | undefined,
  now = Date.now(),
): ExpiryCountdown | null {
  const expires = expiryTimestamp(expiresAt);
  if (expires == null || expires <= now) return null;
  const left = expires - now;
  if (left >= DAY_MS) return { unit: 'days', count: Math.floor(left / DAY_MS) };
  if (left >= HOUR_MS) return { unit: 'hours', count: Math.floor(left / HOUR_MS) };
  return { unit: 'withinHour' };
}

/** True when the offer is still valid. Missing expiry is live; unparseable expiry is not. */
export function isLiveDeal(deal: Pick<Deal, 'expires_at'>, now = Date.now()): boolean {
  const raw = deal.expires_at;
  if (raw == null || raw === '') return true;
  const expires = expiryTimestamp(raw);
  if (expires == null) return false;
  return expires >= now;
}

export function isExpiringSoon(
  deal: Pick<Deal, 'expires_at'>,
  now = Date.now(),
  windowMs = EXPIRING_SOON_MS,
): boolean {
  const expires = expiryTimestamp(deal.expires_at);
  if (expires == null) return false;
  return expires >= now && expires - now <= windowMs;
}

const DISCOUNT_BUCKETS: DiscountBucket[] = [20, 40, 60];

export const normalizeDealText = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export function storeLabel(deal: Pick<Deal, 'retailer' | 'retailerDisplayName'>): string {
  return deal.retailerDisplayName || deal.retailer;
}

export function sameStore(a: string, b: string): boolean {
  return normalizeDealText(a) === normalizeDealText(b);
}

export function matchesStore(
  deal: Pick<Deal, 'retailer' | 'retailerDisplayName'>,
  vendor: string | null,
): boolean {
  if (!vendor?.trim()) return true;
  const needle = normalizeDealText(vendor);
  return (
    normalizeDealText(deal.retailer) === needle ||
    normalizeDealText(deal.retailerDisplayName ?? '') === needle
  );
}

/** Highest 1–100 percent mentioned in the name or description; null if none. */
export function maxDiscountPercent(deal: Pick<Deal, 'name' | 'description'>): number | null {
  const text = `${deal.name} ${deal.description ?? ''}`;
  let max: number | null = null;
  for (const match of text.matchAll(/(\d+)\s*%/g)) {
    const n = Number(match[1]);
    if (n >= 1 && n <= 100) {
      max = max == null ? n : Math.max(max, n);
    }
  }
  return max;
}

export function isLatestDeal(
  deal: Pick<Deal, 'effective_at'>,
  now = Date.now(),
  windowMs = LATEST_DEAL_MS,
): boolean {
  if (!deal.effective_at) return false;
  const started = Date.parse(deal.effective_at);
  if (Number.isNaN(started) || started > now) return false;
  return now - started <= windowMs;
}

export function matchesDealFilters(
  deal: DealFilterFields,
  filters: DealFilters,
  now = Date.now(),
): boolean {
  if (!matchesStore(deal, filters.vendor)) return false;
  if (filters.hasCode && !deal.code) return false;
  if (filters.expiringSoon && !isExpiringSoon(deal, now)) return false;
  if (filters.latest && !isLatestDeal(deal, now)) return false;
  if (filters.discount != null) {
    const pct = maxDiscountPercent(deal);
    if (pct == null || pct < filters.discount) return false;
  }
  return true;
}

export type DealSearchFields = Pick<Deal, 'retailer' | 'name' | 'description'>;

const DEAL_SEARCH_OPTIONS = {
  keys: ['retailer', 'name', 'description'],
  threshold: 0.3,
  ignoreLocation: true,
  minMatchCharLength: 4,
};

/**
 * One Fuse index per list, not per row: Fuse scores each record on its own fields, so a shared
 * index matches the same rows as one Fuse per row.
 */
export function indexDealSearch(deals: DealSearchFields[]): (query: string) => boolean[] {
  const texts = deals.map((deal) =>
    [deal.retailer, deal.name, deal.description ?? ''].map(normalizeDealText).join(' '),
  );
  const fuse = new Fuse(
    deals.map((deal) => ({ ...deal, description: deal.description ?? '' })),
    DEAL_SEARCH_OPTIONS,
  );
  return (query) => {
    const normQuery = normalizeDealText(query);
    if (!normQuery) return deals.map(() => false);
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const normWords = words.map(normalizeDealText);
    const fuzzyHits = words.map((word) => new Set(fuse.search(word).map((hit) => hit.refIndex)));
    return texts.map(
      (text, i) =>
        text.includes(normQuery) ||
        normWords.every((word) => text.includes(word)) ||
        fuzzyHits.every((hits) => hits.has(i)),
    );
  };
}

export type StoreOption = { slug: string; label: string; imageUrl?: string | null };

/** Retailer key used as `/deals/:store` (lowercase). */
export function storeSlug(deal: Pick<Deal, 'retailer'>): string {
  return deal.retailer.toLowerCase();
}

export function uniqueStores(
  deals: Pick<Deal, 'retailer' | 'retailerDisplayName' | 'image_url'>[],
): StoreOption[] {
  const bySlug = new Map<string, { label: string; imageUrl: string | null }>();
  for (const deal of deals) {
    const slug = storeSlug(deal);
    if (!slug) continue;
    const imageUrl = deal.image_url ?? null;
    const cur = bySlug.get(slug);
    if (!cur) {
      bySlug.set(slug, { label: storeLabel(deal), imageUrl });
    } else if (!cur.imageUrl && imageUrl) {
      cur.imageUrl = imageUrl;
    }
  }
  return [...bySlug.entries()]
    .map(([slug, { label, imageUrl }]) => ({ slug, label, imageUrl }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

export const AMAZON_STORE_SLUG = 'amazon';
export const AMAZON_STORE_LABEL = 'Amazon';

export function isAmazonStore(vendor: string | null): boolean {
  return vendor != null && normalizeDealText(vendor) === AMAZON_STORE_SLUG;
}

/** Stores whose page stays live without a deals.json row. Mirrors PINNED_DEAL_STORES in service/util/deals.ts. */
export const PINNED_DEAL_STORES: StoreOption[] = [
  { slug: AMAZON_STORE_SLUG, label: AMAZON_STORE_LABEL },
];

export function withPinnedStores(stores: StoreOption[]): StoreOption[] {
  const missing = PINNED_DEAL_STORES.filter(
    (pinned) => !stores.some((store) => store.slug === pinned.slug),
  );
  return [...stores, ...missing].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );
}

/** Stores with the most offers, for crawlable related links. */
export function popularStores(
  deals: Pick<Deal, 'retailer' | 'retailerDisplayName'>[],
  limit = 8,
): StoreOption[] {
  const counts = new Map<string, { label: string; n: number }>();
  for (const deal of deals) {
    const slug = storeSlug(deal);
    if (!slug) continue;
    const cur = counts.get(slug);
    if (cur) cur.n += 1;
    else counts.set(slug, { label: storeLabel(deal), n: 1 });
  }
  return [...counts.entries()]
    .sort(
      (a, b) =>
        b[1].n - a[1].n || a[1].label.localeCompare(b[1].label, undefined, { sensitivity: 'base' }),
    )
    .slice(0, limit)
    .map(([slug, { label }]) => ({ slug, label }));
}

/** Resolve a path segment or legacy `?vendor=` value (key or display name) to a retailer slug. */
export function resolveStoreSlug(
  deals: Pick<Deal, 'retailer' | 'retailerDisplayName'>[],
  needle: string,
): string | null {
  const n = normalizeDealText(needle);
  if (!n) return null;
  const match = deals.find(
    (deal) =>
      normalizeDealText(deal.retailer) === n ||
      normalizeDealText(deal.retailerDisplayName ?? '') === n,
  );
  return match ? storeSlug(match) : null;
}

/** Feed store, else pinned store. Must stay in step with dealsPathStoreSlug in service/util/deals.ts. */
export function dealsPathStoreSlug(
  deals: Pick<Deal, 'retailer' | 'retailerDisplayName'>[],
  needle: string,
): string | null {
  const n = normalizeDealText(needle);
  return (
    resolveStoreSlug(deals, needle) ??
    PINNED_DEAL_STORES.find((store) => store.slug === n)?.slug ??
    null
  );
}

/** Path for the deals page, optionally store-scoped. Drops leftover `vendor` query params. */
export function dealsPath(storeSlugValue: string | null, searchParams?: URLSearchParams): string {
  const path = storeSlugValue ? `/deals/${encodeURIComponent(storeSlugValue)}` : '/deals';
  if (!searchParams) return path;
  const params = new URLSearchParams(searchParams);
  params.delete('vendor');
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function dealsHref(storeSlugValue: string | null, from?: string | null): string {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  return dealsPath(storeSlugValue, params);
}

/** Navbar / nag: store URL only when that supported vendor is in the deals feed. */
export function dealsNavHref(
  storeSlugValue: string | null,
  hasStoreDeals: boolean,
  from?: string | null,
): string {
  return dealsHref(hasStoreDeals ? storeSlugValue : null, from);
}

const NEW_DEAL_MS = 24 * 60 * 60 * 1000;

export function startedWithinDay(start: string | null | undefined, now = Date.now()): boolean {
  if (!start) return false;
  const elapsed = now - new Date(start).getTime();
  return elapsed >= 0 && elapsed < NEW_DEAL_MS;
}

/** Kebab-case deal name for `/deals/:store/:deal` (max 80 chars). */
export function slugifyDealName(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-')
      .slice(0, 80) || 'deal'
  );
}

type DealSlugFields = {
  retailer: string;
  name: string;
  dealClickBase?: string | null;
  link?: string | null;
  code?: string | null;
  description?: string | null;
  image_url?: string | null;
  effective_at?: string | null;
  expires_at?: string | null;
  featured?: boolean;
  school?: boolean;
};

function fnv1aHex(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0').slice(0, 6);
}

// dealClickBase is one storewide URL at most retailers, so it cannot separate same-name offers
// on its own. Coerced like the server copy, which reads untyped feed JSON.
function dealSlugDiscriminator(deal: DealSlugFields): string {
  return [
    deal.name,
    deal.code,
    deal.link,
    deal.dealClickBase,
    deal.description,
    deal.image_url,
    deal.effective_at,
    deal.expires_at,
  ]
    .map((field) => (typeof field === 'string' ? field : ''))
    .concat(String(deal.featured), String(deal.school))
    .join('\0');
}

/**
 * Unique slug per store. Same-name collisions get a content-derived suffix, so feed order cannot
 * reassign a permalink. Byte-identical rows have nothing left to hash and fall through to an
 * order-dependent ordinal; ingestion drops them, so that path should stay unreachable.
 */
export function assignDealSlugs(deals: DealSlugFields[]): string[] {
  const rows = deals.map((deal) => {
    const store = deal.retailer.toLowerCase();
    const base = slugifyDealName(deal.name);
    return { store, base, key: `${store}\0${base}`, hash: fnv1aHex(dealSlugDiscriminator(deal)) };
  });
  const counts = new Map<string, number>();
  for (const row of rows) {
    counts.set(row.key, (counts.get(row.key) ?? 0) + 1);
  }
  const taken = new Set<string>();

  return rows.map((row) => {
    const candidate = (counts.get(row.key) ?? 1) > 1 ? `${row.base}-${row.hash}` : row.base;
    let slug = candidate;
    for (let n = 2; taken.has(`${row.store}\0${slug}`); n++) {
      slug = `${candidate}-${n}`;
    }
    taken.add(`${row.store}\0${slug}`);

    return slug;
  });
}

export function dealPermalink(storeSlugValue: string, dealSlug: string): string {
  return `/deals/${encodeURIComponent(storeSlugValue)}/${encodeURIComponent(dealSlug)}`;
}

/**
 * React key for a deal card. `path` alone collides when same-name offers share a
 * `dealClickBase`, and those duplicate keys stay mounted after a filter change.
 */
export function dealListKey(
  deal: { code?: string | null; link?: string | null },
  path: string,
  index: number,
): string {
  return `${path}\0${deal.code ?? ''}\0${deal.link ?? ''}\0${index}`;
}

export function findDealBySlug<T extends DealSlugFields>(
  deals: T[],
  storeSlugValue: string,
  dealSlug: string,
): T | undefined {
  const slugs = assignDealSlugs(deals);
  const store = storeSlugValue.toLowerCase();
  const slug = dealSlug.toLowerCase();
  const i = deals.findIndex((d, idx) => d.retailer.toLowerCase() === store && slugs[idx] === slug);
  return i === -1 ? undefined : deals[i];
}

export function parseDiscountParam(raw: string | null): DiscountBucket | null {
  const n = Number(raw);
  return DISCOUNT_BUCKETS.includes(n as DiscountBucket) ? (n as DiscountBucket) : null;
}

export function parseCodeParam(raw: string | null): boolean {
  return raw === '1';
}

export function parseExpiringParam(raw: string | null): boolean {
  return raw === '1';
}
